/**
 * /checkin/profile-extras — post-verification DOB / country / time zone.
 * Skippable. Follows the calm minimalism of the Check In shell.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckInShell, CHECKIN_PALETTE as P } from "./_shell";
import { checkinApi, getToken } from "@/lib/checkinApi";

const COUNTRIES = [
  ["US", "United States"], ["CA", "Canada"], ["GB", "United Kingdom"],
  ["AU", "Australia"], ["NZ", "New Zealand"], ["IE", "Ireland"],
  ["DE", "Germany"], ["FR", "France"], ["ES", "Spain"], ["IT", "Italy"],
  ["NL", "Netherlands"], ["SE", "Sweden"], ["NO", "Norway"],
  ["ZA", "South Africa"], ["NG", "Nigeria"], ["KE", "Kenya"],
  ["IN", "India"], ["SG", "Singapore"], ["JP", "Japan"], ["KR", "South Korea"],
  ["MX", "Mexico"], ["BR", "Brazil"], ["AR", "Argentina"],
  ["OTHER", "Other"],
];

export default function CheckInProfileExtras() {
  const navigate = useNavigate();
  const [dob, setDob] = useState("");
  const [country, setCountry] = useState("");
  const [tz, setTz] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!getToken()) { navigate("/checkin/signin?next=/checkin/profile-extras"); return; }
    // Auto-detect timezone from the browser
    try {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detected) setTz(detected);
    } catch { /* ignore */ }
    checkinApi.verifyStatus().then((s) => {
      const p = s?.profile || {};
      if (p.date_of_birth) setDob(p.date_of_birth);
      if (p.country) setCountry(p.country);
      if (p.time_zone) setTz(p.time_zone);
      if (!p.verified_phone) navigate("/checkin/verify", { replace: true });
    }).catch(() => {});
  }, [navigate]);

  const save = async (skip = false) => {
    setError("");
    setBusy(true);
    try {
      const payload = skip ? {} : {
        date_of_birth: dob || undefined,
        country: country || undefined,
        time_zone: tz || undefined,
      };
      if (!skip) await checkinApi.setProfileExtras(payload);
      navigate("/checkin/home", { replace: true });
    } catch (e) {
      setError(e.message || "Couldn't save. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <CheckInShell title="Complete your profile" testid="checkin-profile-extras-page">
      <div className="pt-2">
        <div className="text-xs uppercase tracking-widest" style={{ color: P.purple }}>
          Phase 1.5 · A little more about you
        </div>
        <h1 className="text-2xl mt-1 font-semibold" data-testid="extras-headline">
          Help Dr. C personalize your journey.
        </h1>
        <p className="text-sm mt-2" style={{ color: "#5F5766" }}>
          These stay private and are used only to make check-ins feel more personal. You can skip and add them later.
        </p>

        <div className="mt-6 space-y-4">
          <label className="block text-sm">
            <div className="text-xs uppercase tracking-widest mb-1" style={{ color: P.purple }}>
              Date of birth
            </div>
            <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                   data-testid="extras-dob-input"
                   className="w-full px-3 py-3 rounded-xl"
                   style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }} />
          </label>
          <label className="block text-sm">
            <div className="text-xs uppercase tracking-widest mb-1" style={{ color: P.purple }}>
              Country
            </div>
            <select value={country} onChange={(e) => setCountry(e.target.value)}
                    data-testid="extras-country-select"
                    className="w-full px-3 py-3 rounded-xl bg-white"
                    style={{ border: `1px solid ${P.silverSoft}`, color: P.ink }}>
              <option value="">Select country</option>
              {COUNTRIES.map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <div className="text-xs uppercase tracking-widest mb-1" style={{ color: P.purple }}>
              Time zone
            </div>
            <input value={tz} onChange={(e) => setTz(e.target.value)}
                   placeholder="America/New_York"
                   data-testid="extras-tz-input"
                   className="w-full px-3 py-3 rounded-xl"
                   style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }} />
            <div className="text-[11px] mt-1" style={{ color: "#9E97AB" }}>
              Auto-detected from your device. Adjust if you prefer.
            </div>
          </label>
        </div>

        {error && (
          <div className="mt-4 text-sm p-3 rounded-xl"
               style={{ background: "#FEEEF1", color: P.danger, border: `1px solid ${P.danger}22` }}
               data-testid="extras-error">
            {error}
          </div>
        )}

        <div className="mt-6 space-y-3">
          <button onClick={() => save(false)} disabled={busy}
                  data-testid="extras-save-btn"
                  className="w-full py-3 rounded-xl text-white font-medium disabled:opacity-60"
                  style={{ background: P.purple }}>
            {busy ? "Saving…" : "Save & continue"}
          </button>
          <button onClick={() => save(true)} disabled={busy}
                  data-testid="extras-skip-btn"
                  className="w-full py-2 text-sm"
                  style={{ color: P.purple }}>
            Skip for now
          </button>
        </div>
      </div>
    </CheckInShell>
  );
}
